import { useEffect, useRef, useState } from "react";
import api from "../services/api";
import Swal from "sweetalert2";
import useCart from "../hooks/useCart";
import Select from "react-select";

export default function PosForm({ onSuccess }) {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [taxRate, setTaxRate] = useState(0);

  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [showPayModal, setShowPayModal] = useState(false);
  const [cash, setCash] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingCart, setPendingCart] = useState(null);

  const inputRef = useRef(null);

  const {
    cart,
    setCart,
    subtotal,
    addProductByCode,
    addProduct,
    updateQuantity,
    removeItem,
    clearCart,
  } = useCart();

  // Load clients
  useEffect(() => {
    api.get("/clients").then((res) => {
      const data = res.data.data ?? res.data;
      setClients(data);
    });
  }, []);

  // Load company tax rate
  useEffect(() => {
    api.get("/company/info").then((res) => {
      const rate = res.data?.data?.sales_tax_rate ?? res.data?.sales_tax_rate ?? 0;
      setTaxRate(Number(rate) || 0);
    });
  }, []);

  // Read cart from localStorage on mount (coming from a table order)
  useEffect(() => {
    const cartFromMesa = localStorage.getItem("cart_from_mesa");
    if (cartFromMesa) {
      try {
        setPendingCart(JSON.parse(cartFromMesa));
        localStorage.removeItem("cart_from_mesa");
      } catch {
        localStorage.removeItem("cart_from_mesa");
      }
    }
  }, []);

  // Apply pending cart once clients are ready
  useEffect(() => {
    if (pendingCart && clients.length > 0) {
      const { items, client_id } = pendingCart;
      setCart(items);
      if (client_id) {
        const client = clients.find((c) => c.id === client_id);
        if (client) setSelectedClient({ value: client.id, label: client.nombre });
      }
      setPendingCart(null);
    }
  }, [pendingCart, clients]);

  // Load products
  useEffect(() => {
    api.get("/products").then((res) => {
      const data = res.data.data ?? res.data;
      setProducts(data);
    });
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!search) {
      setResults([]);
      return;
    }

    const q = search.toLowerCase();

    setResults(
      products
        .filter((p) => {
          const matchCode = p.codigo?.toLowerCase().includes(q);
          const matchName = p.nombre?.toLowerCase().includes(q);
          return matchCode || matchName;
        })
        .slice(0, 6)
    );
  }, [search, products]);

  const handleKeyDown = async (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      if (results.length > 0) {
        addProduct(results[0]);
      } else {
        await addProductByCode(search);
      }

      setSearch("");
      setResults([]);
    }
  };

  const handleSelect = (product) => {
    addProduct(product);
    setSearch("");
    setResults([]);
    inputRef.current.focus();
  };

  // Calculate tax based on each cart item's is_taxable flag
  const calculateTax = (cartItems, rate) => {
    return cartItems.reduce((acc, item) => {
      const lineTotal = (Number(item.cantidad) || 1) * (Number(item.precio_unitario) || 0);
      return acc + (item.is_taxable ? lineTotal * rate : 0);
    }, 0);
  };

  const salesTax = calculateTax(cart, taxRate);
  const total = subtotal + salesTax;

  const buildSafePayload = (clientId, cartItems, cashInput, rate) => {
    const cashAmount = parseFloat(cashInput) || 0;

    const details = cartItems.map((item) => ({
      product_id: Number(item.product_id),
      presentation_id: item.presentation_id || null,
      cantidad: Number(item.cantidad) || 1,
      precio_unitario: Number(item.precio_unitario) || 0,
    }));

    if (details.length === 0) {
      throw new Error("The cart must have at least one product");
    }

    const lineSubtotal = details.reduce((acc, i) => acc + i.cantidad * i.precio_unitario, 0);
    const tax = calculateTax(cartItems, rate);
    const montoFinal = lineSubtotal + tax;

    const fechaMySQL = new Date().toISOString().slice(0, 19).replace("T", " ");

    return {
      client_id: clientId ? Number(clientId) : null,
      fecha_venta: fechaMySQL,
      details,
      subtotal: lineSubtotal,
      monto_final: montoFinal,
      estado: "PENDIENTE",
      cash: cashAmount,
      change: cashAmount - montoFinal,
    };
  };

  const submitSale = async () => {
    if (cart.length === 0) {
      return Swal.fire("Error", "Cart is empty", "warning");
    }

    if (Number(cash) < total) {
      return Swal.fire("Error", "Insufficient amount", "warning");
    }

    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const payload = buildSafePayload(selectedClient?.value, cart, cash, taxRate);
      const saleRes = await api.post("/sales", payload);
      const saleData = saleRes.data.data;

      onSuccess({
        ...saleData,
        cash: payload.cash,
        change: payload.change,
      });

      clearCart();
      setCash("");
      setShowPayModal(false);
    } catch (error) {
      console.error("SALE ERROR:", error.response?.data || error);
      Swal.fire("Error", error.response?.data?.message || "Error processing the sale", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const clientOptions = clients.map((c) => ({
    value: c.id,
    label: c.nombre,
  }));

  const selectedClientFull = selectedClient ? clients.find((c) => c.id === selectedClient.value) : null;

  return (
    <>
      {/* Client (optional) */}
      <div className="form-group">
        <label className="form-label">Customer (optional)</label>
        <Select
          options={clientOptions}
          value={selectedClient}
          onChange={setSelectedClient}
          placeholder="Select a customer..."
          isClearable
          styles={{
            control: (base) => ({
              ...base,
              borderColor: "#e2e8f0",
              borderWidth: "2px",
              borderRadius: "10px",
              padding: "4px",
              "&:hover": { borderColor: "#667eea" },
            }),
          }}
        />
      </div>

      {/* POS Input */}
      <div className="form-group" style={{ position: "relative" }}>
        <label className="form-label">Search Product</label>
        <input
          ref={inputRef}
          className="form-control"
          placeholder="Scan or search for a product..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
        />

        {results.length > 0 && (
          <div style={{ position: "absolute", width: "100%", zIndex: 1000, marginTop: "4px" }}>
            <div className="card" style={{ maxHeight: "300px", overflowY: "auto" }}>
              {results.map((p) => (
                <div
                  key={p.id}
                  onClick={() => handleSelect(p)}
                  style={{
                    padding: "12px 16px",
                    cursor: "pointer",
                    borderBottom: "1px solid #e2e8f0",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) => (e.target.style.background = "#f7fafc")}
                  onMouseLeave={(e) => (e.target.style.background = "white")}
                >
                  <strong style={{ color: "var(--primary-color)" }}>{p.codigo}</strong>
                  {" — "}
                  <span style={{ color: "var(--text-primary)" }}>{p.nombre}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Cart */}
      <div className="table-container mt-4">
        <table className="table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Description</th>
              <th style={{ width: 100, textAlign: "center" }}>Qty</th>
              <th style={{ width: 120, textAlign: "right" }}>Unit Price</th>
              <th style={{ width: 120, textAlign: "right" }}>Total</th>
              <th style={{ width: 80, textAlign: "center" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {cart.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center" style={{ padding: "32px" }}>
                  <p style={{ color: "var(--text-secondary)", margin: 0 }}>
                    The cart is empty. Scan or search for products to add.
                  </p>
                </td>
              </tr>
            )}
            {cart.map((p) => (
              <tr key={p.uniqueKey}>
                <td>
                  <strong style={{ color: "var(--text-primary)" }}>{p.codigo}</strong>
                </td>
                <td>{p.nombre}</td>
                <td style={{ textAlign: "center" }}>
                  <input
                    type="number"
                    min="1"
                    className="form-control text-center"
                    value={p.cantidad}
                    onChange={(e) => updateQuantity(p.uniqueKey, e.target.value)}
                    style={{ padding: "8px" }}
                  />
                </td>
                <td style={{ textAlign: "right" }}>${p.precio_unitario.toFixed(2)}</td>
                <td style={{ textAlign: "right" }}>
                  <strong style={{ color: "var(--primary-color)" }}>${p.subtotal.toFixed(2)}</strong>
                </td>
                <td className="text-center">
                  <button className="btn btn-danger btn-sm" onClick={() => removeItem(p.uniqueKey)} title="Remove item">
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div style={{ marginTop: "24px", marginBottom: "16px" }}>
        <div className="d-flex justify-content-between" style={{ marginBottom: "6px" }}>
          <span style={{ color: "var(--text-secondary)" }}>Subtotal</span>
          <span>${subtotal.toFixed(2)}</span>
        </div>
        <div className="d-flex justify-content-between" style={{ marginBottom: "6px" }}>
          <span style={{ color: "var(--text-secondary)" }}>Sales Tax ({(taxRate * 100).toFixed(2)}%)</span>
          <span>${salesTax.toFixed(2)}</span>
        </div>
        <div className="d-flex justify-content-between align-items-center mt-2">
          <h3 style={{ color: "var(--text-primary)", margin: 0 }}>Total:</h3>
          <h2 style={{ color: "var(--primary-color)", margin: 0, fontSize: "32px", fontWeight: "700" }}>
            ${total.toFixed(2)}
          </h2>
        </div>
      </div>

      <button
        className="btn btn-success btn-lg"
        disabled={cart.length === 0}
        onClick={() => setShowPayModal(true)}
        style={{ width: "100%" }}
      >
        💳 Charge / Checkout
      </button>

      {/* Payment Modal */}
      {showPayModal && (
        <>
          <div className="modal fade show d-block">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content card">
                <div className="card-header">
                  <h3 className="card-title" style={{ margin: 0 }}>💰 Checkout</h3>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setShowPayModal(false);
                      setCash("");
                    }}
                  >
                    ✕
                  </button>
                </div>

                <div className="card-body">
                  <div className="alert alert-info mb-3">
                    <strong>Customer:</strong> {selectedClientFull?.nombre || "Walk-in"}
                  </div>

                  <div className="alert alert-success">
                    <div className="d-flex justify-content-between">
                      <span>Subtotal</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Sales Tax ({(taxRate * 100).toFixed(2)}%)</span>
                      <span>${salesTax.toFixed(2)}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontWeight: 700, marginTop: "6px" }}>
                      <strong>Total to pay:</strong>
                      <strong>${total.toFixed(2)}</strong>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Amount Received</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="form-control"
                      placeholder="0.00"
                      value={cash}
                      autoFocus
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setCash(value);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          if (cash && Number(cash) >= total && !isSubmitting) {
                            submitSale();
                          }
                        }
                      }}
                      style={{ fontSize: "18px", fontWeight: "600" }}
                    />
                  </div>

                  {cash !== "" && (
                    <div className={`alert ${Number(cash) >= total ? "alert-success" : "alert-danger"} mt-3`}>
                      <strong>
                        {Number(cash) >= total
                          ? `✓ Change: $${(Number(cash) - total).toFixed(2)}`
                          : "⚠ Insufficient amount"}
                      </strong>
                    </div>
                  )}
                </div>

                <div
                  className="card-body"
                  style={{ borderTop: "2px solid #e2e8f0", display: "flex", gap: "12px", paddingTop: "var(--spacing-md)" }}
                >
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowPayModal(false);
                      setCash("");
                    }}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-success"
                    disabled={!cash || Number(cash) < total || isSubmitting}
                    onClick={submitSale}
                    style={{ flex: 1 }}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Processing...
                      </>
                    ) : (
                      "✓ Checkout"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </>
  );
}